import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, where, getDocs, doc, getDoc, setDoc } from '../firebaseConfig';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import Card from '@mui/material/Card';
import MDBox from '../components/MDBox';
import Divider from '@mui/material/Divider';
import TimelineIcon from '@mui/icons-material/Timeline';
import MaterialTooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import MDTypography from '../components/MDTypography';
import { useTheme } from '@mui/material/styles';
import { usePeriod } from '../components/PeriodContext';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const getWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  return Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
};

const TeamWinRateGraphOverall = ({ teamId, userIDs }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [monthlyWinRates, setMonthlyWinRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchTeamWinRates = async () => {
      setLoading(true);
      if (!teamId || !userIDs || userIDs.length === 0) {
        console.log('TeamWinRateGraphOverall: Missing teamId or userIDs', { teamId, userIDs });
        setMonthlyWinRates([]);
        setHasData(false);
        setLoading(false);
        return;
      }

      try {
        const oneEightyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 180))
          .toISOString()
          .split('T')[0]; // e.g., "2024-11-19"
        const sevenDaysAgo = new Date(new Date().setDate(new Date().getDate() - 7));

        // Aggregate win rates across all users
        let aggregatedMonthlyWinRates = Array(6).fill(0);
        let userCountWithData = 0;

        for (const userId of userIDs) {
          const userDataRef = doc(db, 'teamData', teamId, 'users', userId);
          const userDataDoc = await getDoc(userDataRef);

          let monthlyWinRates = Array(6).fill(0);
          let hasCachedData = false;

          if (userDataDoc.exists()) {
            const userData = userDataDoc.data();
            if (userData.lastUpdated?.toDate() >= sevenDaysAgo && userData.monthlyWinRates) {
              monthlyWinRates = userData.monthlyWinRates;
              hasCachedData = true;
              console.log(`TeamWinRateGraphOverall: Using cached win rates for user ${userId}:`, monthlyWinRates);
            }
          }

          if (!hasCachedData) {
            let allTrades = [];
            const accountsQuery = query(collection(db, `Users/${userId}/tradeAccounts`));
            const accountsDocs = await getDocs(accountsQuery);
            const tradeAccountIds = accountsDocs.docs.map((doc) => doc.id).concat('default');

            for (const accountId of tradeAccountIds) {
              const tradesQuery = query(
                collection(db, `Users/${userId}/tradeAccounts/${accountId}/trades`),
                where('status', '==', showMissedTrades ? 'Missed' : 'Closed'),
                where('date', '>=', oneEightyDaysAgo)
              );
              const tradesSnapshot = await getDocs(tradesQuery);
              const tradesData = tradesSnapshot.docs
                .map((doc) => ({
                  id: doc.id,
                  date: doc.data().date,
                  returns: parseFloat(doc.data().returns) || 0,
                  tags: Array.isArray(doc.data().tags) ? doc.data().tags : [],
                }))
                .filter((trade) => {
                  if (!trade.date || !/^\d{4}-\d{2}-\d{2}$/.test(trade.date)) {
                    console.warn(`Invalid date format for trade ${trade.id}:`, trade.date);
                    return false;
                  }
                  const tradeDate = new Date(trade.date);
                  if (isNaN(tradeDate.getTime())) {
                    console.warn(`Invalid date object for trade ${trade.id}:`, trade.date);
                    return false;
                  }
                  return true;
                });
              allTrades = allTrades.concat(tradesData);
            }

            // Aggregate by month
            const monthlyWins = Array(6).fill(0);
            const monthlyTrades = Array(6).fill(0);

            allTrades.forEach((trade) => {
              const tradeDate = new Date(trade.date);
              const monthIndex = Math.floor((new Date() - tradeDate) / (1000 * 60 * 60 * 24 * 30));
              if (monthIndex >= 0 && monthIndex < 6) {
                monthlyTrades[monthIndex]++;
                if (trade.returns > 0) {
                  monthlyWins[monthIndex]++;
                }
              }
            });

            monthlyWinRates = monthlyTrades.map((total, idx) =>
              total > 0 ? ((monthlyWins[idx] / total) * 100).toFixed(2) : 0
            );

            await setDoc(userDataRef, {
              monthlyWinRates,
              lastUpdated: new Date(),
              period: 'past 180 days',
            }, { merge: true });
            console.log(`TeamWinRateGraphOverall: Stored new win rates for user ${userId} in teamData/${teamId}/users/${userId}:`, monthlyWinRates);
          }

          // Aggregate for team-wide display
          if (monthlyWinRates.some(rate => parseFloat(rate) > 0)) {
            userCountWithData++;
            monthlyWinRates.forEach((rate, idx) => {
              aggregatedMonthlyWinRates[idx] += parseFloat(rate);
            });
          }
        }

        // Average across users
        const teamMonthlyWinRates = aggregatedMonthlyWinRates.map((sum) =>
          userCountWithData > 0 ? (sum / userCountWithData).toFixed(2) : 0
        );

        setMonthlyWinRates(teamMonthlyWinRates);
        setHasData(teamMonthlyWinRates.some(rate => parseFloat(rate) > 0));
        console.log('TeamWinRateGraphOverall: Aggregated team win rates:', teamMonthlyWinRates);
      } catch (error) {
        console.error('TeamWinRateGraphOverall: Error fetching trades:', error);
        setMonthlyWinRates([]);
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamWinRates();
  }, [teamId, userIDs, showMissedTrades]);

  const filterTrades = (trades) => {
    return trades.filter((trade) => {
      const tradeDate = trade.date ? new Date(trade.date) : null;
      if (!tradeDate || isNaN(tradeDate.getTime())) return false;
      const matchesPeriod = () => {
        if (selectedPeriod === 'today') {
          return tradeDate.toDateString() === new Date().toDateString();
        } else if (selectedPeriod === 'this-week') {
          return getWeek(tradeDate) === getWeek(new Date()) && tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'this-month') {
          return tradeDate.getMonth() === new Date().getMonth() && tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'this-year') {
          return tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'pick-month') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-year') {
          return tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-month-year') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        }
        return true; // all-time
      };
      const matchesTag = selectedTag === 'all' || (Array.isArray(trade.tags) && trade.tags.includes(selectedTag));
      return matchesPeriod() && matchesTag;
    });
  };

  const getMonthLabels = () => {
    const labels = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      labels.push(date.toLocaleString('default', { month: 'short', year: 'numeric' })); // e.g., "Dec 2024"
    }
    return labels;
  };

  const data = {
    labels: getMonthLabels(),
    datasets: [
      {
        label: 'Team Win Rate (%)',
        data: monthlyWinRates,
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: { ticks: { autoSkip: true, maxTicksLimit: 6, maxRotation: 45, minRotation: 45 } },
      y: { beginAtZero: true, min: 0, max: 100, ticks: { stepSize: 10 } },
    },
    plugins: {
      legend: { display: true },
      tooltip: { callbacks: { label: (context) => `Win Rate: ${context.parsed.y.toFixed(1)}%` } },
    },
  };

  const tooltipContent = 'Team Win Rate shows the average percentage of winning trades across all team members’ accounts over the past 180 days, aggregated monthly, updated weekly.';

  if (loading) {
    return (
      <Card style={{ minHeight: '300px' }}>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
          <MDTypography variant="caption" color="text">
            Loading trades...
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  if (!hasData) {
    return (
      <Card style={{ minHeight: '300px' }}>
        <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
          <MDBox display="flex" justifyContent="space-between">
            <MDBox
              variant="gradient"
              bgColor="light"
              color="dark"
              coloredShadow="light"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="4rem"
              height="4rem"
              mt={-3}
            >
              <TimelineIcon fontSize="medium" color="inherit" />
            </MDBox>
            <MDBox textAlign="right" lineHeight={1.25}>
              <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
                Team Win Rate Progress (All Members & Accounts)
              </MDTypography>
              <MaterialTooltip
                title={tooltipContent}
                placement="bottom-end"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      border: `1px solid ${theme.palette.divider}`,
                      p: 1.5,
                      maxWidth: '250px',
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                    },
                  },
                }}
              >
                <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
              </MaterialTooltip>
            </MDBox>
          </MDBox>
          <Divider />
          <MDBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            textAlign="center"
            px={2}
          >
            <>
              <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
              <MDTypography variant="h6" color="text">
                No win rate data available
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Team members need to complete trades to see win rate analysis
              </MDTypography>
            </>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <TimelineIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Team Win Rate Progress (All Members & Accounts)
            </MDTypography>
            <MaterialTooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </MaterialTooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox height="100%">
          <Line data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

TeamWinRateGraphOverall.propTypes = {
  teamId: PropTypes.string.isRequired,
  userIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default TeamWinRateGraphOverall;